import { Box, Chip, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import React from 'react';

import { marketService } from '../../../services/marketService.js';
import { mockSectors, type SectorPerformance } from '../../market/mockData.js';
import type { WidgetProps } from '../types/index.js';
import { HorizontalPager } from './components/HorizontalPager.tsx';
import { useProfileNavigation } from '../hooks/useProfileNavigation.js';

const formatPercent = (value: number) => `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;

const barWidth = (change: number) => {
  const bounded = Math.min(Math.abs(change), 3); // clamp to 3%
  return `${(bounded / 3) * 100}%`;
};

export const SectorOverviewWidget: React.FC<WidgetProps> = ({ widget, onUpdateOutputs, onUpdateState }) => {
  const theme = useTheme();
  const [sectors, setSectors] = React.useState<SectorPerformance[]>(() => mockSectors);
  const [quoteMap, setQuoteMap] = React.useState<Record<string, number>>({}); // symbol -> changePercent
  const { openProfileByTicker } = useProfileNavigation();

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await marketService.getSectors().catch(() => []);
        if (!cancelled && Array.isArray(data) && data.length > 0) {
          const mapped = data.map((s: any) => ({
            id: s.id,
            name: s.name,
            changePercent: Number(s.changePercent),
            leaders: Array.isArray(s.leaders) ? s.leaders : [],
            laggards: Array.isArray(s.laggards) ? s.laggards : [],
          })) as SectorPerformance[];
          setSectors(mapped);
          onUpdateState({ loading: false, data: mapped });
          return;
        }
      } catch {}
      if (!cancelled) {
        setSectors(mockSectors);
        onUpdateState({ loading: false, data: mockSectors });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedOutput = React.useMemo(() => {
    return widget.outputs.find((output) => output.id === 'selectedSector')?.value as SectorPerformance | undefined;
  }, [widget.outputs]);

  const [selectedId, setSelectedId] = React.useState<string | null>(selectedOutput?.id ?? null);

  React.useEffect(() => {
    onUpdateState({ loading: false, data: sectors });
  }, [sectors, onUpdateState]);

  // Live price wiring for leader/laggard chips
  React.useEffect(() => {
    let cancelled = false;
    const fetchQuotes = async () => {
      try {
        const symbols = Array.from(
          new Set(
            sectors.flatMap((s) => [...(s.leaders || []), ...(s.laggards || [])]).filter(Boolean)
          )
        );
        if (symbols.length === 0) return;
        const data = await marketService.getMarketDataForSymbols(symbols).catch(() => []);
        if (cancelled) return;
        const map: Record<string, number> = {};
        for (const d of data) map[d.symbol] = Number(d.changePercent) || 0;
        setQuoteMap(map);
      } catch {}
    };
    fetchQuotes();
    const id = setInterval(fetchQuotes, 60000); // refresh every minute
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [sectors]);

  React.useEffect(() => {
    if (!selectedOutput && sectors.length > 0) {
      if (selectedId !== sectors[0].id) {
        setSelectedId(sectors[0].id);
        onUpdateOutputs({ selectedSector: sectors[0] });
      }
    }
  }, [sectors, onUpdateOutputs, selectedOutput, selectedId]);

  React.useEffect(() => {
    if (selectedOutput && selectedOutput.id !== selectedId) {
      setSelectedId(selectedOutput.id);
    }
  }, [selectedOutput, selectedId]);

  const handleSelect = React.useCallback(
    (sector: SectorPerformance) => {
      setSelectedId(sector.id);
      onUpdateOutputs({ selectedSector: sector });
    },
    [onUpdateOutputs]
  );

  const createSymbolClickHandler = React.useCallback(
    (symbol: string) => (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      openProfileByTicker(symbol);
    },
    [openProfileByTicker]
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Remove internal duplicate title/subtitle to maximize content */}
      <HorizontalPager ariaLabel="Sectors overview scroller">
        {sectors.map((sector) => {
          const selected = sector.id === selectedId;
          const changePositive = sector.changePercent >= 0;
          return (
            <Box
              key={sector.id}
              onClick={() => handleSelect(sector)}
              sx={{
                width: 350,
                minWidth: 150,
                height: 150,
                borderRadius: 1,
                border: '1px solid',
                borderColor: selected ? 'primary.main' : 'divider',
                backgroundColor: selected ? 'action.selected' : 'background.paper',
                p: 1,
                cursor: 'pointer',
                display: 'grid',
                gridTemplateRows: 'auto 1fr auto',
                alignItems: 'start',
                overflow: 'hidden',
                transition: 'border-color 0.2s ease, background-color 0.2s ease',
                '&:hover': {
                  borderColor: selected ? 'primary.light' : theme.palette.primary.light,
                },
                scrollSnapAlign: 'start',
              }}
            >
              <Box sx={{ minHeight: 58 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {sector.name}
                </Typography>
                <Typography
                  variant="body2"
                  color={changePositive ? 'success.main' : 'error.main'}
                  sx={{ fontWeight: 600 }}
                >
                  {formatPercent(sector.changePercent)}
                </Typography>
              </Box>
              <Box
                sx={{
                  mt: 1,
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: theme.palette.action.hover,
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    left: changePositive ? '50%' : `calc(50% - ${barWidth(sector.changePercent)})`,
                    top: 0,
                    height: '100%',
                    width: barWidth(sector.changePercent),
                    backgroundColor: changePositive ? theme.palette.success.main : theme.palette.error.main,
                    transition: 'width 0.3s ease',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    left: '50%',
                    top: 0,
                    width: 1,
                    height: '100%',
                    backgroundColor: theme.palette.divider,
                  }}
                />
              </Box>
              <Box sx={{ pt: 0.5 }}>
                <Tooltip title={`Leaders: ${sector.leaders.join(', ')}`} arrow placement="top">
                  <Stack direction="row" spacing={0.5} sx={{ overflow: 'hidden' }}>
                    {sector.leaders.slice(0, 3).map((sym) => {
                      const cp = quoteMap[sym];
                      const color = typeof cp === 'number' ? (cp >= 0 ? 'success' : 'error') : 'default';
                      return (
                        <Chip
                          key={sym}
                          size="small"
                          label={`${sym}${typeof cp === 'number' ? ` ${formatPercent(cp)}` : ''}`}
                          color={color as any}
                          variant="outlined"
                          onClick={createSymbolClickHandler(sym)}
                          onMouseDown={(event) => event.stopPropagation()}
                          onPointerDown={(event) => event.stopPropagation()}
                          sx={{ cursor: 'pointer' }}
                        />
                      );
                    })}
                  </Stack>
                </Tooltip>
                <Tooltip title={`Laggards: ${sector.laggards.join(', ')}`} arrow placement="bottom">
                  <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, overflow: 'hidden' }}>
                    {sector.laggards.slice(0, 3).map((sym) => {
                      const cp = quoteMap[sym];
                      const color = typeof cp === 'number' ? (cp >= 0 ? 'success' : 'error') : 'default';
                      return (
                        <Chip
                          key={sym}
                          size="small"
                          label={`${sym}${typeof cp === 'number' ? ` ${formatPercent(cp)}` : ''}`}
                          color={color as any}
                          variant="outlined"
                          onClick={createSymbolClickHandler(sym)}
                          onMouseDown={(event) => event.stopPropagation()}
                          onPointerDown={(event) => event.stopPropagation()}
                          sx={{ cursor: 'pointer' }}
                        />
                      );
                    })}
                  </Stack>
                </Tooltip>
              </Box>
            </Box>
          );
        })}
      </HorizontalPager>
    </Box>
  );
};
